import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, Lock, Shield, Key } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";

export default function ProLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("credentials"); // "credentials" | "2fa"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [tempToken, setTempToken] = useState("");
  const [tfaCode, setTfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");

  // Resume a 2FA challenge handed off from the Landing SIGN IN box.
  // The Landing form stashes `pro_login_temp_token` in sessionStorage and
  // navigates to /pro/login?step=2fa. We pick that up and jump straight to
  // the 2FA step so the user doesn't have to re-enter their password.
  useEffect(() => {
    if (searchParams.get("step") === "2fa") {
      const handoff = sessionStorage.getItem("pro_login_temp_token");
      if (handoff) {
        setTempToken(handoff);
        setStep("2fa");
        sessionStorage.removeItem("pro_login_temp_token");
      }
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    try {
      const { data } = await api.post("/pro/login", {
        username: username.trim(),
        password,
      });

      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setStep("2fa");
      } else {
        toast.success("Logged in");
        navigate("/pro/inbox");
      }
    } catch (err) {
      console.error("[pro-login] login error", err);
      toast.error(getErrorMessage(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (useRecovery) {
        const { data } = await api.post("/pro/2fa/recovery-login", {
          temp_token: tempToken,
          recovery_code: recoveryCode.trim(),
        });
        if (data.recovery_codes_remaining <= 2) {
          toast.warning(`Only ${data.recovery_codes_remaining} recovery codes remaining`);
        }
      } else {
        await api.post("/pro/2fa/verify-login", {
          temp_token: tempToken,
          code: tfaCode,
        });
      }
      toast.success("Logged in");
      navigate("/pro/inbox");
    } catch (err) {
      console.error("[pro-login] 2fa error", err);
      toast.error(getErrorMessage(err, "Verification failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto rounded-xl bg-zinc-900 flex items-center justify-center mb-4">
            {step === "credentials" ? <Lock size={22} className="text-white" /> : <Shield size={22} className="text-white" />}
          </div>
          <h1 className="text-xl font-semibold text-[#111111]">
            {step === "credentials" ? "PRO Login" : "Two-factor verification"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {step === "credentials"
              ? "Sign in to your QuickInbox PRO account"
              : useRecovery
                ? "Enter one of your recovery codes"
                : "Enter the code from your authenticator app"}
          </p>
        </div>

        {step === "credentials" ? (
          <form onSubmit={handleLogin} className="bg-white rounded-xl border border-[#E5E5E5] p-6 space-y-4 shadow-sm">
            <div className="space-y-1.5">
              <Label htmlFor="pro-username" className="text-xs text-zinc-500">Email address or username</Label>
              <Input
                id="pro-username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._@-]/g, ""))}
                placeholder="you@mail.quickinbox.app or username"
                className="font-mono"
                autoFocus
                autoComplete="username"
                data-testid="pro-login-username"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pro-password" className="text-xs text-zinc-500">Password</Label>
              <div className="relative">
                <Input
                  id="pro-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pr-10"
                  data-testid="pro-login-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#888888]">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading || !username.trim() || !password} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10" data-testid="pro-login-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Log in"}
            </Button>

            <div className="text-center pt-1">
              <button type="button" onClick={() => navigate("/pro/recover")} className="text-xs text-blue-600 hover:text-blue-600" data-testid="pro-login-forgot">
                Forgot password?
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handle2FA} className="bg-white rounded-xl border border-[#E5E5E5] p-6 space-y-4 shadow-sm">
            {!useRecovery ? (
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">6-digit code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={tfaCode}
                  onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-lg font-mono tracking-[0.3em]"
                  autoFocus
                  data-testid="pro-login-2fa-code"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Recovery code</Label>
                <Input
                  type="text"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  className="text-center font-mono tracking-wider"
                  autoFocus
                  data-testid="pro-login-recovery-code"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (!useRecovery && tfaCode.length !== 6) || (useRecovery && !recoveryCode.trim())}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10"
              data-testid="pro-login-2fa-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Verify"}
            </Button>

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => { setUseRecovery(!useRecovery); setTfaCode(""); setRecoveryCode(""); }}
                className="text-xs text-blue-600 hover:text-blue-600 flex items-center gap-1"
                data-testid="pro-login-toggle-recovery"
              >
                <Key size={11} /> {useRecovery ? "Use authenticator" : "Use recovery code"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("credentials"); setTempToken(""); setTfaCode(""); setRecoveryCode(""); setUseRecovery(false); }}
                className="text-xs text-zinc-500 hover:text-[#888888]"
              >
                Back to login
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-xs text-zinc-500 mt-4">
          Don't have a PRO account?{" "}
          <button onClick={() => navigate("/")} className="text-blue-600 hover:text-blue-600">Get started</button>
        </p>
      </div>
    </div>
  );
}
